import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { Button } from '../ui/Button';
import { PageHeader } from '../ui/PageHeader';
import { useAuth } from '../../contexts/AuthContext';

const ClubCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { isOrganizer, isLoading } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [region, setRegion] = useState('');
  const [clubType, setClubType] = useState('other');
  const [website, setWebsite] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (andReview: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiService.saveClub({
        name,
        description,
        region,
        club_type: clubType,
        website,
      });
      const club = (res as any).data;
      if (andReview && club?.id) {
        await apiService.submitClub(club.id);
      }
      navigate(`/clubs/${club.slug}`);
    } catch (e: any) {
      setError(e.message || 'Failed to create club');
    } finally {
      setBusy(false);
    }
  };

  if (!isLoading && !isOrganizer) {
    return (
      <div className="page max-w-xl mx-auto px-4 py-16">
        <p className="kicker mb-2">Community</p>
        <h1 className="heading-section mb-3">Organizer access required</h1>
        <p className="text-muted mb-6">
          Starting a club requires organizer access. Ask a platform admin for an organizer invite code or capability grant.
        </p>
        <Link to="/clubs" className="text-orange-400 hover:text-orange-300">
          Back to clubs
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-100">
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          eyebrow="Community"
          title="Create a club"
          description="Clubs start as drafts. Send for review before public listing. Verification is separate from approval."
          backLink={{ label: 'Back to Clubs', to: '/clubs' }}
        />
        {error ? (
          <div role="alert" className="mb-4 text-red-300">
            {error}
          </div>
        ) : null}
        <div className="form-section space-y-4">
          <div>
            <label htmlFor="club-name" className="input-label">
              Name
            </label>
            <input
              id="club-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="club-description" className="input-label">
              Description
            </label>
            <textarea
              id="club-description"
              className="input input-textarea"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="club-region" className="input-label">
              Region
            </label>
            <input
              id="club-region"
              className="input"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="club-type" className="input-label">
              Type
            </label>
            <select
              id="club-type"
              className="input"
              value={clubType}
              onChange={(e) => setClubType(e.target.value)}
            >
              {['marque', 'regional', 'track', 'show', 'lifestyle', 'other'].map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="club-website" className="input-label">
              Website
            </label>
            <input
              id="club-website"
              className="input"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button type="button" variant="secondary" disabled={busy || !name.trim()} onClick={() => submit(false)}>
              Save draft
            </Button>
            <Button type="button" variant="primary" disabled={busy || !name.trim()} onClick={() => submit(true)}>
              Save and submit for review
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClubCreatePage;
